import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import Breadcrumb from "@/components/Breadcrumb";
import CreateUserDialog from "@/components/admin/CreateUserDialog";
import AdminUserRow from "@/components/admin/AdminUserRow";
import IncidentNotifyConfigCard from "@/components/IncidentNotifyConfigCard";
import SafetyBaselineCard from "@/components/SafetyBaselineCard";
import PhotoNamingCard from "@/components/admin/PhotoNamingCard";
import { apiError, isUserLocked } from "@/lib/adminFormat";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldAlert, ScrollText, Search, X, Trash2,
  CheckSquare, Square, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [lockedOnly, setLockedOnly] = useState(false);

  // Wrapped in useCallback so the mount-effect's exhaustive-deps lint
  // can include it without an empty-array escape hatch. Stable across
  // renders since it captures no component state.
  const refresh = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Apply search + role + locked filters in one memoized pass so the
  // list stays snappy even at 100+ users.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (lockedOnly && !isUserLocked(u)) return false;
      if (needle) {
        const hay = `${u.full_name || ""} ${u.email || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, lockedOnly]);

  const lockedCount = useMemo(() => users.filter(isUserLocked).length, [users]);

  // ---- Bulk selection ----
  // `selectedIds` is a Set of user ids the admin has ticked for a bulk
  // action. `selectableIds` re-derives from `filtered` (deletable rows
  // only — excluding the actor's own row) so the "select all" checkbox
  // respects the current filter view and never tries to delete the
  // actor.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const selectableIds = useMemo(
    () => filtered.filter((u) => u.id !== me?.id).map((u) => u.id),
    [filtered, me?.id]
  );
  // Active selection is intersected with the currently-visible set —
  // changing the filter shouldn't silently include rows the admin can't
  // see.
  const visibleSelectedCount = useMemo(
    () => filtered.filter((u) => selectedIds.has(u.id)).length,
    [filtered, selectedIds]
  );
  const allVisibleChecked =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someVisibleChecked = visibleSelectedCount > 0 && !allVisibleChecked;

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const toggleAllVisible = () => {
    const next = new Set(selectedIds);
    if (allVisibleChecked) {
      selectableIds.forEach((id) => next.delete(id));
    } else {
      selectableIds.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // Type-to-confirm a separate phrase ("DELETE 7 USERS") so muscle-
    // memory enter-press doesn't fire on a bulk wipe.
    const phrase = `DELETE ${ids.length} USER${ids.length === 1 ? "" : "S"}`;
    const typed = window.prompt(
      `You're about to permanently delete ${ids.length} user account${ids.length === 1 ? "" : "s"}.\n\n` +
      `Notifications and project recipient memberships for each user are also removed. Audit-log history is kept intact.\n\n` +
      `Type "${phrase}" to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim() !== phrase) {
      toast.error("Confirmation phrase didn't match — bulk delete cancelled.");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk-delete", { user_ids: ids });
      const skippedReasons = data.outcomes.filter((o) => !o.deleted).map((o) => o.reason);
      let msg = `Deleted ${data.deleted} user${data.deleted === 1 ? "" : "s"}`;
      if (data.notifications_purged) msg += ` · ${data.notifications_purged} notification(s) purged`;
      if (skippedReasons.length) {
        msg += ` · ${skippedReasons.length} skipped (${[...new Set(skippedReasons)].join(", ")})`;
      }
      toast.success(msg);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Bulk delete failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed to update role"));
    }
  };

  const updateActive = async (id, is_active) => {
    try {
      await api.patch(`/users/${id}`, { is_active });
      toast.success(is_active ? "User activated" : "User disabled");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed to update status"));
    }
  };

  const unlock = async (id) => {
    try {
      await api.post(`/users/${id}/unlock`);
      toast.success("User unlocked");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed to unlock"));
    }
  };

  const deleteUser = async (u) => {
    // Hard-delete confirmation is intentionally chunky — this nukes the
    // account AND every notification addressed to them. Type-to-confirm
    // raises the cost just enough to prevent muscle-memory mistakes.
    const expected = (u.email || "").trim();
    const typed = window.prompt(
      `This will permanently delete ${u.full_name} (${expected}).\n\n` +
      `Notifications and project recipient memberships for this user are also removed.\n` +
      `Audit-log history is kept intact.\n\n` +
      `Type the email to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== expected.toLowerCase()) {
      toast.error("Email did not match — delete cancelled.");
      return;
    }
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      toast.success(
        `Deleted ${expected} · ${data.notifications_purged || 0} notification(s) purged`,
      );
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed to delete user"));
    }
  };

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "Users" }]} />
          <h1
            className="font-display font-black text-3xl sm:text-4xl tracking-tight"
            data-testid="admin-heading"
          >
            User Management
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            asChild variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800 font-display tracking-wider"
            data-testid="open-health"
          >
            <Link to="/admin/health"><Activity className="w-4 h-4 mr-2 text-emerald-400" />HEALTH</Link>
          </Button>
          <Button
            asChild variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800 font-display tracking-wider"
            data-testid="open-audit-log"
          >
            <Link to="/audit"><ScrollText className="w-4 h-4 mr-2" />AUDIT LOG</Link>
          </Button>
          <CreateUserDialog onCreated={refresh} />
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800 mb-3">
        <CardContent className="p-3 flex flex-wrap items-end gap-3" data-testid="admin-filter-toolbar">
          <div className="flex-1 min-w-[220px]">
            <Label htmlFor="admin-search" className="text-xs uppercase tracking-wider text-slate-400">
              Search
            </Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <Input
                id="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or email"
                className="bg-slate-950 border-slate-700 pl-8 pr-8"
                data-testid="admin-search-input"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                  data-testid="admin-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger
                className="bg-slate-950 border-slate-700 mt-1 w-44"
                data-testid="admin-role-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="foreman">Foreman</SelectItem>
                <SelectItem value="pm">Project Manager</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs uppercase tracking-wider text-slate-400">
              Locked only
              {lockedCount > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center text-[10px] font-bold bg-red-700 text-white rounded-full px-1.5 py-0.5"
                  data-testid="admin-locked-count"
                >
                  {lockedCount}
                </span>
              )}
            </Label>
            <div className="h-10 flex items-center">
              <Switch
                checked={lockedOnly}
                onCheckedChange={setLockedOnly}
                data-testid="admin-locked-toggle"
              />
            </div>
          </div>
          <div className="ml-auto text-xs text-slate-400" data-testid="admin-result-count">
            {filtered.length} of {users.length} users
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0 divide-y divide-slate-800" data-testid="admin-user-list">
          {/* Header row — selection toggle + bulk action toolbar. Renders
              the "select all visible" master checkbox and morphs into a
              compact action bar once any row is checked. */}
          {filtered.length > 0 && (
            <div
              className={`p-3 flex flex-wrap items-center gap-3 sticky top-0 z-10 ${
                selectedIds.size > 0
                  ? "bg-red-950/50 border-b border-red-900"
                  : "bg-slate-900"
              }`}
              data-testid="admin-bulk-toolbar"
            >
              <button
                type="button"
                onClick={toggleAllVisible}
                aria-label={allVisibleChecked ? "Deselect all" : "Select all visible"}
                aria-checked={allVisibleChecked ? "true" : someVisibleChecked ? "mixed" : "false"}
                role="checkbox"
                className="text-slate-300 hover:text-slate-100"
                data-testid="admin-select-all"
              >
                {allVisibleChecked || someVisibleChecked ? (
                  <CheckSquare className={`w-4 h-4 ${someVisibleChecked ? "opacity-60" : ""}`} />
                ) : (
                  <Square className="w-4 h-4" />
                )}
              </button>
              {selectedIds.size === 0 ? (
                <div className="text-xs text-slate-400 select-none">
                  Tick rows to bulk-delete seasonal sub-crews.
                </div>
              ) : (
                <>
                  <div className="text-xs font-display tracking-wider text-red-200" data-testid="admin-bulk-count">
                    {selectedIds.size} SELECTED
                  </div>
                  <Button
                    size="sm"
                    onClick={bulkDelete}
                    disabled={bulkBusy}
                    className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
                    data-testid="admin-bulk-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    {bulkBusy ? "Deleting…" : `DELETE ${selectedIds.size}`}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    disabled={bulkBusy}
                    className="text-slate-300 hover:text-slate-100"
                    data-testid="admin-bulk-clear"
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
          )}
          {filtered.map((u) => (
            <AdminUserRow
              key={u.id}
              user={u}
              isSelf={me?.id === u.id}
              checked={selectedIds.has(u.id)}
              onToggle={toggleOne}
              onRoleChange={updateRole}
              onActiveChange={updateActive}
              onUnlock={unlock}
              onDelete={deleteUser}
            />
          ))}
          {filtered.length === 0 && (
            <div className="p-8 text-center text-slate-400" data-testid="admin-empty-state">
              <ShieldAlert className="w-6 h-6 mx-auto mb-2 opacity-50" />
              {users.length === 0
                ? "No users found."
                : "No users match the current filters."}
              {(search || roleFilter !== "all" || lockedOnly) && users.length > 0 && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSearch(""); setRoleFilter("all"); setLockedOnly(false); }}
                    className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                    data-testid="admin-clear-filters"
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cross-project incident notification config — sits below the
          user list so admins land on the user table first (higher
          frequency task) and this is available when they scroll. */}
      <div className="mt-4 space-y-4">
        <SafetyBaselineCard />
        <IncidentNotifyConfigCard />
        <PhotoNamingCard />
      </div>
    </AppShell>
  );
}
